import { useState, useEffect, useCallback } from "react";

export interface MediaDeviceInfo {
  deviceId: string;
  label: string;
  kind: "audioinput" | "audiooutput";
}

interface UseMediaDevicesReturn {
  microphones: MediaDeviceInfo[];
  speakers: MediaDeviceInfo[];
  selectedMicrophoneId: string | null;
  selectedSpeakerId: string | null;
  selectMicrophone: (deviceId: string) => void;
  selectSpeaker: (deviceId: string) => void;
  hasPermission: boolean;
  refresh: () => Promise<void>;
}

/**
 * Hook to manage audio input/output devices
 * Works with both React and Preact
 */
export function useMediaDevices(): UseMediaDevicesReturn {
  const [microphones, setMicrophones] = useState<MediaDeviceInfo[]>([]);
  const [speakers, setSpeakers] = useState<MediaDeviceInfo[]>([]);
  const [selectedMicrophoneId, setSelectedMicrophoneId] = useState<
    string | null
  >(null);
  const [selectedSpeakerId, setSelectedSpeakerId] = useState<string | null>(
    null,
  );
  const [hasPermission, setHasPermission] = useState(false);

  const refresh = useCallback(async () => {
    try {
      const devices = await navigator.mediaDevices.enumerateDevices();

      const mics: MediaDeviceInfo[] = devices
        .filter((d) => d.kind === "audioinput")
        .map((d) => ({
          deviceId: d.deviceId,
          label: d.label || `Microphone ${d.deviceId.slice(0, 8)}`,
          kind: "audioinput" as const,
        }));

      const spks: MediaDeviceInfo[] = devices
        .filter((d) => d.kind === "audiooutput")
        .map((d) => ({
          deviceId: d.deviceId,
          label: d.label || `Speaker ${d.deviceId.slice(0, 8)}`,
          kind: "audiooutput" as const,
        }));

      const hasPerm = devices.some((d) => d.label !== "");

      setMicrophones(mics);
      setSpeakers(spks);
      setHasPermission(hasPerm);

      // Auto-select first device if none selected
      if (!selectedMicrophoneId && mics.length > 0 && mics[0]) {
        setSelectedMicrophoneId(mics[0].deviceId);
      }
      if (!selectedSpeakerId && spks.length > 0 && spks[0]) {
        setSelectedSpeakerId(spks[0].deviceId);
      }
    } catch (error) {
      console.error("Error enumerating devices:", error);
    }
  }, [selectedMicrophoneId, selectedSpeakerId]);

  const selectMicrophone = useCallback((deviceId: string) => {
    setSelectedMicrophoneId(deviceId);
  }, []);

  const selectSpeaker = useCallback((deviceId: string) => {
    setSelectedSpeakerId(deviceId);
  }, []);

  useEffect(() => {
    refresh();

    const handleDeviceChange = () => refresh();
    navigator.mediaDevices?.addEventListener(
      "devicechange",
      handleDeviceChange,
    );

    return () => {
      navigator.mediaDevices?.removeEventListener(
        "devicechange",
        handleDeviceChange,
      );
    };
  }, [refresh]);

  return {
    microphones,
    speakers,
    selectedMicrophoneId,
    selectedSpeakerId,
    selectMicrophone,
    selectSpeaker,
    hasPermission,
    refresh,
  };
}
